import { Injectable, inject } from '@angular/core';

import { CONFIG_PORT, type IConfigPort } from './config.port';
import { MD5_PORT, type IMd5Port } from './md5.port';
import { ROOT_SCOPE_PORT, type IRootScopePort } from './root-scope.port';
import { STORAGE_PORT, type IStoragePort } from './storage.port';

/**
 * Map of granted permissions, keyed by permission name for O(1) lookup.
 * Set to `false` when the user has no permissions stored yet.
 */
export type PermissionsMap = Record<string, string> | false;

/**
 * PermissionsService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/permissions/permissions.service.js
 *
 * Exposed to AngularJS via `downgradeInjectable` under the same name
 * `'PermissionsService'`, so the 112 existing consumers in b2b-ui keep
 * working without modification (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  permissions: PermissionsMap = false;

  private rootScope = inject<IRootScopePort>(ROOT_SCOPE_PORT);
  private config = inject<IConfigPort>(CONFIG_PORT);
  private storage = inject<IStoragePort>(STORAGE_PORT);
  private md5 = inject<IMd5Port>(MD5_PORT);

  init(): boolean {
    return true;
  }

  store(permissions: string[]): boolean {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return false;
    }

    this.permissions = this.hash(permissions);

    const currentChecksum = this.config.get('app', 'rbacChecksum');
    if (!currentChecksum) {
      this.config.set({ type: 'app', name: 'rbacChecksum', value: this.fetchMD5() });
    }

    return true;
  }

  hash(permissions: string[]): Record<string, string> {
    const sorted = [...permissions].sort();
    const map: Record<string, string> = {};
    for (const perm of sorted) {
      map[perm] = perm;
    }
    return map;
  }

  check(permissions: string | string[] | null | undefined, throwable?: boolean): boolean {
    let isAllowed = true;

    if (permissions != null && permissions !== '') {
      const userPerms = this.permissions || {};
      const list = ([] as string[]).concat(permissions as string | string[]);
      isAllowed = list.some((perm) => Object.prototype.hasOwnProperty.call(userPerms, perm));
    }

    if (!isAllowed && throwable) {
      this.rootScope.$broadcast('PermissionsInterceptorFactory::error', { type: 'permission' });
    }

    return isAllowed;
  }

  fetch(): PermissionsMap {
    return this.permissions;
  }

  fetchMD5(): string {
    return this.md5.createHash(JSON.stringify(this.fetch()));
  }

  hasPermissions(): boolean {
    if (!this.permissions) {
      return false;
    }
    for (const key in this.permissions) {
      if (Object.prototype.hasOwnProperty.call(this.permissions, key)) {
        return true;
      }
    }
    return false;
  }

  refresh(scope?: { $emit: (event: string) => void } | null): void {
    scope?.$emit?.('Permissions::refresh');
  }

  clearChecksum(): void {
    this.config.clear('app', 'rbacChecksum', true);
  }

  clearAll(): void {
    this.clearChecksum();
    this.storage.clearAll();
  }

  normalizePermissions(permissions: unknown): string[] {
    if (typeof permissions === 'string') {
      return [permissions];
    }
    if (Array.isArray(permissions)) {
      return permissions as string[];
    }
    return [];
  }

  every(permissions: string | string[]): boolean {
    const userPerms = this.permissions || {};
    return this.normalizePermissions(permissions).every((perm) =>
      Object.prototype.hasOwnProperty.call(userPerms, perm),
    );
  }

  some(permissions: string | string[]): boolean {
    const userPerms = this.permissions || {};
    return this.normalizePermissions(permissions).some((perm) =>
      Object.prototype.hasOwnProperty.call(userPerms, perm),
    );
  }
}
